/**
 * Presentation labels for the raw `payment_method` / `payment_status` values the
 * reports API returns.
 *
 * PAYMENT_METHOD_LABEL_KEYS covers the eight methods that exist in `tickets`
 * today. Anything else — a method the backend adds later — renders raw on
 * purpose, so an unknown value stays visible in the table instead of
 * disappearing. Adding or changing a label is one entry in the map plus one key
 * in every file under src/assets/i18n.
 */

/** Resolves an i18n key to its label. Keeps this module free of Angular deps. */
export type TranslateFn = (key: string) => string;

/**
 * Maps, not plain objects: a lookup like `'constructor'` on an object literal
 * would hit Object.prototype and return a function instead of missing.
 *
 * `talo` and `bind` deliberately share the "Transferencia" label.
 */
export const PAYMENT_METHOD_LABEL_KEYS: ReadonlyMap<string, string> = new Map([
  ['talo', 'sales-group.payment-method.talo'],
  ['bind', 'sales-group.payment-method.bind'],
  ['mcp', 'sales-group.payment-method.mcp'],
  ['cash', 'sales-group.payment-method.cash'],
  ['mp', 'sales-group.payment-method.mp'],
  ['pay_at_door', 'sales-group.payment-method.pay-at-door'],
  ['transfer', 'sales-group.payment-method.transfer'],
  ['card', 'sales-group.payment-method.card'],
]);

export const PAYMENT_STATUS_LABEL_KEYS: ReadonlyMap<string, string> = new Map([
  ['1', 'sales-group.payment-status.confirmed'],
  ['0', 'sales-group.payment-status.pending'],
]);

const rawText = (value: string | number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * Friendly payment method label.
 *
 * A missing method (empty string, null, undefined) is its own case and renders
 * as an empty cell: it is not an unknown method, so it reaches neither the raw
 * fallback nor the translation dictionary.
 */
export const paymentMethodLabel = (
  method: string | null | undefined,
  translate: TranslateFn
): string => {
  const raw = rawText(method);
  if (raw === '') {
    return '';
  }

  const key = PAYMENT_METHOD_LABEL_KEYS.get(raw);
  return key ? translate(key) : raw;
};

const TICKET_CARD_UNKNOWN_METHOD_KEY = 'payment-methods.unknown';

/**
 * Payment method label for the ticket cards (dictionary `payment-methods`).
 *
 * ngx-translate returns the key itself when a translation is missing, so a
 * method absent from the dictionary would render as `payment-methods.<method>`.
 * It falls back to the "unknown" label instead.
 */
export const ticketCardPaymentMethodLabel = (
  method: string | null | undefined,
  translate: TranslateFn
): string => {
  const key = `payment-methods.${rawText(method).toLowerCase() || 'unknown'}`;
  const label = translate(key);
  return label === key ? translate(TICKET_CARD_UNKNOWN_METHOD_KEY) : label;
};

/**
 * Confirmado / Pendiente. Accepts the number the API sends today and a string
 * just in case; an unexpected value falls back to its raw text instead of
 * rendering an empty cell.
 */
export const paymentStatusLabel = (
  status: number | string | null | undefined,
  translate: TranslateFn
): string => {
  const raw = rawText(status);
  const key = PAYMENT_STATUS_LABEL_KEYS.get(raw);
  return key ? translate(key) : raw;
};
